import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChefHat, MapPin, Phone, Clock, X, Sparkles } from 'lucide-react';
import Navigation from '../components/Navigation';
import logoLaMeta from '../assets/logo_la_meta.jpg';
import logoLaLanterna from '../assets/Design_sans_titre_(5).png';
import logoCanguro from '../assets/Design_sans_titre_(18).png';

interface Restaurant {
  id: string;
  name: string;
  cuisine: string;
  description: string;
  hours: string;
  phone: string;
  address: string;
  logo: string;
}

function RestaurantsPage() {
  const { t } = useTranslation();
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);

  const restaurants: Restaurant[] = [
    {
      id: '1',
      name: 'La Meta',
      cuisine: 'Ristorante Pizzeria & Breakfast',
      description: 'Located in front of the B&B',
      hours: 'Every day: 10:00 AM - 11:00 PM',
      phone: '+39 320 946 3153',
      address: 'In front of Il Galileo Rooms',
      logo: logoLaMeta
    },
    {
      id: '2',
      name: 'La Lanterna',
      cuisine: 'Ristorante Pizzeria',
      description: 'Located in the historic center near the Tower of Pisa',
      hours: 'Every day: 11:00 AM - 11:00 PM',
      phone: '+39 320 946 3153',
      address: 'Via Santa Maria, 109, 56126 Pisa PI',
      logo: logoLaLanterna
    },
    {
      id: '3',
      name: 'Il Canguro',
      cuisine: 'Restaurant Paninoteca',
      description: 'Located in the historic center near the Tower of Pisa',
      hours: 'Every day: 11:00 AM - 11:00 PM',
      phone: '+39 050 561942',
      address: 'Via Santa Maria, 151, 56126 Pisa PI',
      logo: logoCanguro
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50 to-orange-50">
      <Navigation />

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fadeInUp">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-700 to-yellow-800 rounded-2xl shadow-xl mb-6">
              <ChefHat className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-stone-900 mb-4">
              {t('restaurants.title')}
            </h1>
            <div className="w-32 h-1.5 bg-gradient-to-r from-yellow-700 to-yellow-800 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-stone-700 max-w-3xl mx-auto leading-relaxed">
              {t('restaurants.subtitle')}
            </p>

            <div className="mt-8 inline-flex items-center gap-3 bg-gradient-to-r from-yellow-100 to-yellow-200 px-8 py-4 rounded-full shadow-lg">
              <Sparkles className="w-6 h-6 text-yellow-700" />
              <span className="text-lg font-semibold text-stone-900">10% Discount for All Guests</span>
              <Sparkles className="w-6 h-6 text-yellow-700" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {restaurants.map((restaurant) => (
              <div
                key={restaurant.id}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer border border-stone-100"
                onClick={() => setSelectedRestaurant(restaurant)}
              >
                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-stone-800 to-stone-900">
                  <img
                    src={restaurant.logo}
                    alt={restaurant.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-stone-900 mb-2 group-hover:text-yellow-700 transition-colors">
                    {restaurant.name}
                  </h3>
                  <p className="text-yellow-700 font-medium mb-4">{restaurant.cuisine}</p>

                  <div className="h-px bg-gradient-to-r from-stone-200 via-yellow-300 to-stone-200 mb-5"></div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-sm text-stone-700">
                      <MapPin className="w-4 h-4 text-yellow-700 mt-0.5 flex-shrink-0" />
                      <span className="leading-relaxed">{restaurant.description}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-stone-700">
                      <Clock className="w-4 h-4 text-yellow-700 flex-shrink-0" />
                      <span>{restaurant.hours}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-yellow-700 flex-shrink-0" />
                      <a
                        href={`tel:${restaurant.phone}`}
                        className="text-stone-700 hover:text-yellow-700 transition-colors font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {restaurant.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedRestaurant && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-[slideUp_0.3s_ease-out]">
            <div className="relative h-72 overflow-hidden bg-gradient-to-br from-stone-800 to-stone-900">
              <img
                src={selectedRestaurant.logo}
                alt={selectedRestaurant.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

              <button
                onClick={() => setSelectedRestaurant(null)}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white p-2 rounded-full transition-colors shadow-lg z-10"
              >
                <X className="w-6 h-6 text-stone-900" />
              </button>
            </div>

            <div className="p-8">
              <h2 className="text-3xl font-serif font-bold text-stone-900 mb-2">
                {selectedRestaurant.name}
              </h2>
              <p className="text-yellow-700 text-lg font-medium mb-6">{selectedRestaurant.cuisine}</p>

              <div className="mb-6 bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-700 p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-6 h-6 text-yellow-700" />
                  <span className="text-xl font-bold text-stone-900">10% Discount</span>
                </div>
                <p className="text-stone-700">Exclusive offer for Il Galileo Rooms guests</p>
              </div>

              <div className="space-y-4">
                <div className="bg-stone-50 p-5 rounded-xl border border-stone-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-yellow-100 p-2 rounded-lg">
                      <Clock className="w-5 h-5 text-yellow-700" />
                    </div>
                    <h4 className="font-bold text-stone-900">Opening Hours</h4>
                  </div>
                  <p className="text-stone-700">{selectedRestaurant.hours}</p>
                </div>

                <div className="bg-stone-50 p-5 rounded-xl border border-stone-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-yellow-100 p-2 rounded-lg">
                      <MapPin className="w-5 h-5 text-yellow-700" />
                    </div>
                    <h4 className="font-bold text-stone-900">Location</h4>
                  </div>
                  <p className="text-stone-700">{selectedRestaurant.address}</p>
                </div>

                <div className="bg-gradient-to-r from-yellow-700 to-yellow-800 p-5 rounded-xl text-white">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-yellow-100 mb-1">Phone</p>
                      <a
                        href={`tel:${selectedRestaurant.phone}`}
                        className="text-xl font-bold hover:text-yellow-100 transition-colors"
                      >
                        {selectedRestaurant.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RestaurantsPage;
